import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, COMPANY } from "@/lib/content";
import { PillButton } from "@/components/primitives";

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2.5 group" data-testid="header-logo">
      <span className="relative flex h-9 w-9 items-center justify-center rounded-lg bg-teal/15 border border-teal/40 shadow-[0_0_18px_rgba(0,212,170,0.25)]">
        <span className="text-teal font-heading font-extrabold text-lg leading-none">V</span>
      </span>
      <span className="flex flex-col leading-none">
        <span className="font-heading font-bold text-white tracking-tight text-[15px]">
          {COMPANY.brand}
        </span>
        <span className="text-[9px] uppercase tracking-[0.28em] text-slate-400">
          Technologies
        </span>
      </span>
    </Link>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "bg-navy/80 backdrop-blur-xl backdrop-saturate-150 border-b border-white/10"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8 h-[72px]">
          <Logo />

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.to === "/" ? "home" : link.to.slice(1)}`}
                className={({ isActive }) =>
                  `px-3.5 py-2 text-[13.5px] font-medium rounded-full transition-colors duration-200 ${
                    isActive ? "text-teal" : "text-slate-300 hover:text-white"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:block">
            <PillButton to="/platforms" testid="header-cta" icon="ArrowUpRight">
              Explore Our Platforms
            </PillButton>
          </div>

          <button
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 text-white"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-open"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] lg:hidden flex justify-end"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
          >
            <div className="absolute inset-0 bg-navy/90 backdrop-blur-xl" onClick={() => setOpen(false)} />
            <motion.div
              className="relative h-full w-[86%] max-w-[340px] bg-matte border-l border-white/10 p-6 flex flex-col overflow-y-auto"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="flex items-center justify-between mb-8">
                <Logo />
                <button
                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 text-white"
                  onClick={() => setOpen(false)}
                  aria-label="Close menu"
                  data-testid="mobile-menu-close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="flex flex-col gap-1">
                {NAV_LINKS.map((link, i) => (
                  <motion.div
                    key={link.to}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.06 * i }}
                  >
                    <NavLink
                      to={link.to}
                      data-testid={`mobile-nav-${link.to === "/" ? "home" : link.to.slice(1)}`}
                      className={({ isActive }) =>
                        `block py-3.5 text-lg font-heading font-medium border-b border-white/5 ${
                          isActive ? "text-teal" : "text-white"
                        }`
                      }
                    >
                      {link.label}
                    </NavLink>
                  </motion.div>
                ))}
              </nav>
              <div className="mt-8">
                <PillButton to="/platforms" testid="mobile-header-cta" icon="ArrowUpRight">
                  Explore Our Platforms
                </PillButton>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
